// Compõe o personagem (boneco de papel) num quadro 16×24, com cache por
// aparência + direção + pose + passo.
//
// Ordem das camadas: cabelo de trás, pernas, roupa nas pernas, tronco, roupa, acessório
// de tronco, cabeça, cabelo, acessório de cabeça. 'right' = 'left' espelhado.

import type { Look } from '../../core/look.js';
import type { Dir } from '../../core/world.js';
import { ACCESSORY_BY_ID, BODY_BY_ID, BODIES, HAIR_BY_ID, OUTFIT_BY_ID } from '../art/character/index.js';
import { CLOTH_COLORS, HAIR_COLORS, SKIN_TONES, resolveToken, type Ramp, type Slots } from '../art/palette.js';
import { CHAR_H, CHAR_W, HEAD, LEGS, SIT_DY, TORSO, type ArmPose, type BodyDef, type LegPose, type OverlayDef, type PixelSprite } from '../art/types.js';
import { bake, ctx2d, makeCanvas } from './bake.js';

export type Pose = 'idle' | 'walk' | 'sit' | 'type' | 'raise' | 'sitRaise';

const cache = new Map<string, HTMLCanvasElement>();

function ramp(list: readonly Ramp[], i: number | undefined): Ramp {
  return list[((i ?? 0) % list.length + list.length) % list.length] as Ramp;
}

export function slotsOf(look: Look): Slots {
  return {
    skin: ramp(SKIN_TONES, look.skin),
    hair: ramp(HAIR_COLORS, look.hairColor),
    shirt: ramp(CLOTH_COLORS, look.shirt),
    pants: ramp(CLOTH_COLORS, look.pants),
    shoe: ramp(CLOTH_COLORS, look.shoes),
    acc: ramp(CLOTH_COLORS, look.accColor ?? 5),
  };
}

export function lookKey(look: Look): string {
  return [look.body, look.skin, look.hair, look.hairColor, look.outfit, look.shirt, look.pants, look.shoes, look.accessory ?? '-', look.accColor ?? '-'].join(',');
}

function armOf(pose: Pose): ArmPose {
  if (pose === 'type') return 'type';
  if (pose === 'raise' || pose === 'sitRaise') return 'raise';
  return 'down';
}

function legOf(pose: Pose, step: number): LegPose {
  if (pose === 'sit' || pose === 'type' || pose === 'sitRaise') return 'sit';
  if (pose === 'walk') return step % 2 === 0 ? 'stepA' : 'stepB';
  return 'stand';
}

/** O quadro do personagem (16×24). Pés na linha 23, centro na coluna 8. */
export function characterFrame(look: Look, dir: Dir, pose: Pose, step = 0): HTMLCanvasElement {
  const key = `${lookKey(look)}|${dir}|${pose}|${step % 2}`;
  const hit = cache.get(key);
  if (hit) return hit;

  const d = dir === 'right' ? 'left' : dir;
  const body: BodyDef = BODY_BY_ID.get(look.body) ?? (BODIES[0] as BodyDef);
  const slots = slotsOf(look);
  const slotKey = lookKey(look);
  const arm = armOf(pose);
  const leg = legOf(pose, step);
  const dy = leg === 'sit' ? SIT_DY : pose === 'walk' && step % 2 === 1 ? 1 : 0;

  const frame = makeCanvas(CHAR_W, CHAR_H);
  const ctx = ctx2d(frame);
  const put = (s: PixelSprite | undefined, y: number) => {
    if (s) ctx.drawImage(bake(s, { slots, slotKey }), 0, y);
  };
  const hair: OverlayDef | undefined = HAIR_BY_ID.get(look.hair);
  const outfit = OUTFIT_BY_ID.get(look.outfit);
  const acc = look.accessory ? ACCESSORY_BY_ID.get(look.accessory) : undefined;

  put(hair?.behind?.[d], dy);
  put(body.legs[d][leg], LEGS.y);
  put(outfit?.legs?.[d], LEGS.y);
  put(body.torso[d][arm], TORSO.y + dy);
  put(outfit?.frames[d], TORSO.y + dy);
  if (acc?.anchor === 'torso') put(acc.frames[d], TORSO.y + dy);
  put(body.head[d], HEAD.y + dy);
  put(hair?.frames[d], HEAD.y + dy);
  if (acc?.anchor === 'head') put(acc.frames[d], HEAD.y + dy);
  if (arm === 'raise') raisedArm(ctx, slots, d, dy);

  let out = frame;
  if (dir === 'right') {
    out = makeCanvas(CHAR_W, CHAR_H);
    const flip = ctx2d(out);
    flip.translate(CHAR_W, 0);
    flip.scale(-1, 1);
    flip.drawImage(frame, 0, 0);
  }
  cache.set(key, out);
  return out;
}

/** Antebraço levantado ao lado da cabeça (o tronco só traz o ombro). */
function raisedArm(ctx: CanvasRenderingContext2D, slots: Slots, dir: 'down' | 'up' | 'left', dy: number): void {
  const x = dir === 'left' ? 3 : 12;
  const ink = resolveToken('ink') ?? '#000';
  const cloth = slots.shirt[2];
  const skin = slots.skin[2];
  ctx.fillStyle = ink;
  ctx.fillRect(x - 1, 1 + dy, 4, 11);
  ctx.fillStyle = cloth;
  ctx.fillRect(x, 4 + dy, 2, 7);
  ctx.fillStyle = skin;
  ctx.fillRect(x, 2 + dy, 2, 2);
}
